// Performance V2 — Search Terms tab (#4296). Search query analysis with
// ADDED / EXCLUDED / NONE status tracking against the account's keyword lists.
// Extracted from performance-v2.tsx (#4932 Phase 0.3); no behavior change.
// The search-terms query stays in the shell (the Overview's notable-terms
// widget shares it); column visibility also stays shell-owned because the
// header toolbar's ColumnsPopover and the userSettings persistence live there.
import { Spinner } from '@/components/glitch-ui/spinner';
import { useMemo, useState } from 'react';
import { BarChart3 } from 'lucide-react';
import { ColumnDef } from '@tanstack/react-table';

import type { AccountSearchTerm } from '@/lib/apis';
import { DataTable } from '@/components/glitch-ui/data-table';
import { FilterDropdown } from '@/components/glitch-ui/filter-dropdown';
import { WidgetGrid } from '@/components/glitch-ui/widget-grid';
import type { WidgetConfig } from '@/components/glitch-ui/widget-grid';
import { cn } from '@/lib/utils';
import {
  fmt, MatchTypeBadge, SearchTermStatusBadge, PerformanceCell, EmptyTabState,
} from '../cells';

// Column-picker metadata for the Search Terms table. Same Performance-
// vs-granular split as Keywords. Exported for the shell's header toolbar
// (ColumnsPopover) and the userSettings column-visibility restore.
export const SEARCH_TERMS_TABLE_COLUMNS: { id: string; label: string; pinned?: boolean; defaultHidden?: boolean }[] = [
  { id: 'search_term',   label: 'Search term',     pinned: true },
  { id: 'performance',   label: 'Performance' },
  { id: 'impressions',   label: 'Impressions',     defaultHidden: true },
  { id: 'clicks',        label: 'Clicks',          defaultHidden: true },
  { id: 'conversions',   label: 'Conversions',     defaultHidden: true },
  { id: 'cost',          label: 'Cost',            defaultHidden: true },
  { id: 'ctr',           label: 'CTR' },
  { id: 'avg_cpc',       label: 'Avg. CPC' },
  { id: 'conv_rate',     label: 'Conv. rate' },
  { id: 'cost_per_conv', label: 'Cost/conv' },
];
export const SEARCH_TERMS_COLUMN_IDS = new Set(SEARCH_TERMS_TABLE_COLUMNS.map(c => c.id));
export const SEARCH_TERMS_PINNED_COLUMN_IDS = new Set(SEARCH_TERMS_TABLE_COLUMNS.filter(c => c.pinned).map(c => c.id));
export const SEARCH_TERMS_DEFAULT_VISIBLE_IDS = new Set(SEARCH_TERMS_TABLE_COLUMNS.filter(c => !c.defaultHidden).map(c => c.id));

export function SearchTermsTab({
  terms,
  isLoading,
  visibleColumns,
  currencySymbol,
  layout,
  onReorder,
  onResize,
}: {
  terms: AccountSearchTerm[] | undefined;
  isLoading: boolean;
  visibleColumns: Set<string>;
  currencySymbol: string;
  dateRangeLabel: string;
  dateRangeFilter: { start_date: string; end_date: string };
  layout: WidgetConfig[];
  onReorder: (layout: WidgetConfig[]) => void;
  onResize: (id: string, width: 33 | 50 | 66 | 100) => void;
}) {
  const cs = currencySymbol;
  // #4296 — Search-terms status filter. 'all' shows every row regardless of
  // whether it's been added to a list or excluded; ADDED / EXCLUDED / NONE
  // match search_term_view.status.
  const [searchTermStatusFilter, setSearchTermStatusFilter] = useState<'all' | 'NONE' | 'ADDED' | 'EXCLUDED'>('all');

  // Campaign and match type (#6758). An account can carry well over a thousand
  // terms and the list caps at the most-impressing 2,000, so the status chips
  // alone left the reader scrolling. Both narrow the rows already loaded, as the
  // chips do; nothing new is fetched. Empty means no narrowing, not no rows.
  const [selections, setSelections] = useState<Record<string, string[]>>({ campaign: [], match_type: [] });
  const campaignFilter = selections.campaign ?? [];
  const matchTypeFilter = selections.match_type ?? [];

  // Options come from the rows on screen, so the list only ever offers a value
  // that would return something, with its count beside it.
  const { campaignOptions, matchTypeOptions } = useMemo(() => {
    const campaigns = new Map<string, number>();
    const matchTypes = new Map<string, number>();
    for (const row of terms ?? []) {
      if (row.campaign_name) campaigns.set(row.campaign_name, (campaigns.get(row.campaign_name) ?? 0) + 1);
      const mt = row.match_type ?? 'Unknown';
      matchTypes.set(mt, (matchTypes.get(mt) ?? 0) + 1);
    }
    const byLabel = (a: [string, number], b: [string, number]) => a[0].localeCompare(b[0]);
    return {
      campaignOptions: [...campaigns.entries()].sort(byLabel).map(([value, count]) => ({ value, label: value, count })),
      matchTypeOptions: [...matchTypes.entries()].sort(byLabel).map(([value, count]) => ({ value, label: value, count })),
    };
  }, [terms]);

  // #4296 — Search Terms table columns + status filter, now composed with
  // campaign and match type (#6758): all three narrow together, and the count
  // under the table is what survives all of them.
  const filteredSearchTerms = useMemo<AccountSearchTerm[]>(() => {
    if (!terms) return [];
    return terms.filter(r => {
      if (searchTermStatusFilter !== 'all' && r.status !== searchTermStatusFilter) return false;
      if (campaignFilter.length > 0 && !campaignFilter.includes(r.campaign_name)) return false;
      if (matchTypeFilter.length > 0 && !matchTypeFilter.includes(r.match_type ?? 'Unknown')) return false;
      return true;
    });
  }, [terms, searchTermStatusFilter, campaignFilter, matchTypeFilter]);

  const searchTermColumns: ColumnDef<AccountSearchTerm, unknown>[] = [
    { accessorKey: 'search_term', header: 'Search term', cell: ({ row }) => (
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-gray-900">{row.original.search_term}</span>
          <MatchTypeBadge matchType={row.original.match_type} />
          <SearchTermStatusBadge status={row.original.status} />
        </div>
        <div className="flex items-center gap-1.5 mt-0.5 text-xs text-gray-500">
          <span className="truncate max-w-[140px]">{row.original.campaign_name}</span>
          <span className="text-gray-400">·</span>
          <span className="truncate max-w-[140px]">{row.original.ad_group_name}</span>
          {row.original.keyword && (
            <>
              <span className="text-gray-400">·</span>
              <span className="text-gray-500">matched <span className="text-gray-700 font-medium">{row.original.keyword}</span></span>
              <MatchTypeBadge matchType={row.original.keyword_match_type} />
            </>
          )}
        </div>
      </div>
    )},
    // Consolidated Imp · Clicks · Conv · Cost cell, plus the granular columns
    // it consolidates (default-off via the column picker).
    { id: 'performance', header: 'Performance', enableSorting: false, cell: ({ row }) => (
      <PerformanceCell
        impressions={row.original.impressions}
        clicks={row.original.clicks}
        conversions={row.original.conversions}
        cost={row.original.cost}
        currency={cs}
      />
    )},
    { accessorKey: 'impressions', header: 'Imp.', cell: ({ row }) => row.original.impressions.toLocaleString() },
    { accessorKey: 'clicks', header: 'Clicks', cell: ({ row }) => row.original.clicks.toLocaleString() },
    { accessorKey: 'conversions', header: 'Conv.', cell: ({ row }) => row.original.conversions.toFixed(0) },
    { accessorKey: 'cost', header: 'Cost', cell: ({ row }) => fmt(row.original.cost, cs) },
    { accessorKey: 'ctr', header: 'CTR', cell: ({ row }) => `${(row.original.ctr * 100).toFixed(2)}%` },
    { accessorKey: 'avg_cpc', header: 'Avg. CPC', cell: ({ row }) => fmt(row.original.avg_cpc, cs) },
    { accessorKey: 'conv_rate', header: 'Conv. rate', cell: ({ row }) => `${(row.original.conv_rate * 100).toFixed(1)}%` },
    { accessorKey: 'cost_per_conv', header: 'Cost/conv', cell: ({ row }) => row.original.conversions > 0 ? fmt(row.original.cost_per_conv, cs) : <span className="text-xs text-gray-400">—</span> },
  ];
  const visibleSearchTermColumnDefs = searchTermColumns.filter(c => {
    const id = c.id ?? ((c as { accessorKey?: string }).accessorKey);
    return id ? visibleColumns.has(id) : true;
  });

  return (
    <WidgetGrid layout={layout} onReorder={onReorder} onResize={onResize}>
      {(widgetId, _widgetWidth) => {
        switch (widgetId) {
          case 'search-terms-content': {
            const hasTerms = Array.isArray(terms) && terms.length > 0;
            if (isLoading && !hasTerms) {
              return (
                <div className="flex items-center justify-center py-16">
                  <Spinner size="lg" className="text-gray-400" />
                  <span className="ml-3 text-sm text-gray-500">Loading search terms…</span>
                </div>
              );
            }
            if (!terms || terms.length === 0) {
              return (
                <EmptyTabState
                  icon={BarChart3}
                  title="No Search Terms"
                  description="Search terms will appear here once campaigns have impressions in the selected period."
                />
              );
            }
            // #4302 — CSV export for Search Terms; mirrors the Google Ads UI Search terms report column set.
            return (
              <div className="space-y-3">
                {/* Status filter chips + CSV export. */}
                <div className="flex items-center justify-between gap-2 pt-1 pr-10">
                  <div className="flex items-center gap-1.5 text-xs">
                    <span className="text-gray-500">Filter:</span>
                    {(['all', 'NONE', 'ADDED', 'EXCLUDED'] as const).map(opt => {
                      const count = opt === 'all'
                        ? terms.length
                        : terms.filter(r => r.status === opt).length;
                      const isActive = searchTermStatusFilter === opt;
                      const label = opt === 'all' ? 'All' : opt.charAt(0) + opt.slice(1).toLowerCase();
                      return (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => setSearchTermStatusFilter(opt)}
                          className={cn(
                            'px-2 py-0.5 rounded-full text-[11px] font-medium border transition-colors',
                            isActive
                              ? 'bg-[#290BD9] text-white border-[#290BD9]'
                              : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300',
                          )}
                        >
                          {label} <span className={cn('ml-1', isActive ? 'opacity-80' : 'text-gray-400')}>{count}</span>
                        </button>
                      );
                    })}
                  </div>
                  {/* Campaign and match type (#6758). To the right of the status
                      chips: the chips are the coarse cut a reader makes first,
                      these are the ones that make a thousand-row account
                      workable. */}
                  <div className="flex items-center gap-2">
                    <FilterDropdown
                      sections={[
                        { id: 'campaign', title: 'Campaign', options: campaignOptions },
                        { id: 'match_type', title: 'Match type', options: matchTypeOptions },
                      ]}
                      selectedValues={selections}
                      showDateRange={false}
                      popoverWidth="w-[320px]"
                      activeFilterCount={campaignFilter.length + matchTypeFilter.length}
                      onApply={(filters) => setSelections(filters.selections)}
                    />
                  </div>
                </div>
                <DataTable<AccountSearchTerm>
                  data={filteredSearchTerms}
                  columns={visibleSearchTermColumnDefs}
                  pageSize={50}
                  itemsName="search term"
                  variant="bordered"
                  emptyState="No search terms match the current filter."
                />
                <p className="text-[11px] text-gray-400 px-1">
                  Showing {filteredSearchTerms.length.toLocaleString()} of {terms.length.toLocaleString()} search terms. The list is capped at the most-impressing 2,000 — narrow the date range to drill further.
                </p>
              </div>
            );
          }

          default:
            return null;
        }
      }}
    </WidgetGrid>
  );
}
